import posthog from 'posthog-js';
import Clarity from '@microsoft/clarity';
import { setSentryUser, clearSentryUser, AnalyticsUser } from './sentry';

const isDev = import.meta.env.DEV;

/**
 * Centralized Analytics Module
 * Manages user identification and tracking across PostHog, Sentry, and Clarity
 */

export interface UserProperties {
    id: string;
    email: string;
    name: string;
    company?: string;
    role?: 'admin' | 'user';
    creditBalance?: number;
    createdAt?: string;
    avatar?: string;
}

/**
 * Identify user across all analytics platforms
 * Call this when user logs in or user data changes
 */
export function identifyUser(user: UserProperties): void {
    const userId = user.id.toString();
    
    // === POSTHOG ===
    // Primary analytics - full user properties
    posthog.identify(userId, {
        email: user.email,
        name: user.name,
        company: user.company || '',
        role: user.role || 'user',
        credit_balance: user.creditBalance || 0,
        created_at: user.createdAt,
        has_avatar: !!user.avatar,
        // Computed properties for segmentation
        is_admin: user.role === 'admin',
        has_credits: (user.creditBalance || 0) > 0,
        credit_tier: getCreditTier(user.creditBalance || 0),
    });
    
    // Set person properties that persist
    posthog.people.set({
        $email: user.email,
        $name: user.name,
        company: user.company,
        role: user.role,
        last_seen: new Date().toISOString(),
    });
    
    // === SENTRY ===
    // Error tracking with user context
    setSentryUser({
        id: userId,
        email: user.email,
        username: user.name,
        company: user.company,
        role: user.role,
        creditBalance: user.creditBalance,
        createdAt: user.createdAt,
    });
    
    // === CLARITY ===
    // Session recordings with user identification
    try {
        // Set custom user ID for Clarity
        Clarity.identify(userId, undefined, undefined, user.email);
        
        // Set custom tags for filtering sessions
        Clarity.setTag('user_email', user.email);
        Clarity.setTag('user_name', user.name);
        Clarity.setTag('user_company', user.company || 'none');
        Clarity.setTag('user_role', user.role || 'user');
        Clarity.setTag('has_credits', (user.creditBalance || 0) > 0 ? 'yes' : 'no');
        Clarity.setTag('credit_tier', getCreditTier(user.creditBalance || 0));
        
        if (isDev) console.log('[Analytics] User identified in Clarity:', userId);
    } catch (error) {
        if (isDev) console.warn('[Analytics] Failed to identify user in Clarity:', error);
    }
    
    if (isDev) {
        console.log('[Analytics] User identified across all platforms:', {
            userId,
            email: user.email,
            name: user.name,
            company: user.company,
            role: user.role,
        });
    }
}

/**
 * Reset user identity across all platforms
 * Call this when user logs out
 */
export function resetUser(): void {
    // PostHog - reset to anonymous
    posthog.reset();
    
    // Sentry - clear user context
    clearSentryUser();
    
    // Clarity - no direct reset, but we can clear tags
    try {
        Clarity.setTag('user_email', '');
        Clarity.setTag('user_name', '');
        Clarity.setTag('user_company', '');
        Clarity.setTag('user_role', '');
        Clarity.setTag('has_credits', '');
        Clarity.setTag('credit_tier', '');
    } catch (error) {
        // Clarity might not be initialized
    }
    
    if (isDev) console.log('[Analytics] User reset across all platforms');
}

/**
 * Track a custom event across platforms
 */
export function trackEvent(
    eventName: string, 
    properties?: Record<string, unknown>
): void {
    // PostHog - primary event tracking
    posthog.capture(eventName, properties);
    
    // Clarity - set event as a tag for session filtering
    try {
        Clarity.setTag('last_event', eventName);
        if (properties) {
            // Set key properties as tags (Clarity has limited tag support)
            Object.entries(properties).slice(0, 5).forEach(([key, value]) => {
                if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                    Clarity.setTag(`event_${key}`, String(value));
                }
            });
        }
    } catch (error) {
        // Clarity might not be initialized
    }
}

/**
 * Track page view
 */
export function trackPageView(pageName?: string): void {
    posthog.capture('$pageview', {
        page_name: pageName,
        url: window.location.href,
        path: window.location.pathname,
    });
    
    try {
        Clarity.setTag('current_page', pageName || window.location.pathname);
    } catch (error) {
        // Clarity might not be initialized
    }
}

/**
 * Update user properties without full re-identification
 */
export function updateUserProperties(properties: Partial<UserProperties>): void {
    // PostHog - update person properties
    const posthogProps: Record<string, unknown> = {};
    
    if (properties.creditBalance !== undefined) {
        posthogProps.credit_balance = properties.creditBalance;
        posthogProps.has_credits = properties.creditBalance > 0;
        posthogProps.credit_tier = getCreditTier(properties.creditBalance);
    }
    
    if (properties.company) {
        posthogProps.company = properties.company;
    }
    
    if (properties.role) {
        posthogProps.role = properties.role;
        posthogProps.is_admin = properties.role === 'admin';
    }
    
    if (Object.keys(posthogProps).length > 0) {
        posthog.people.set(posthogProps);
    }
    
    // Update Clarity tags
    try {
        if (properties.creditBalance !== undefined) {
            Clarity.setTag('has_credits', properties.creditBalance > 0 ? 'yes' : 'no');
            Clarity.setTag('credit_tier', getCreditTier(properties.creditBalance));
        }
        if (properties.company) {
            Clarity.setTag('user_company', properties.company);
        }
        if (properties.role) {
            Clarity.setTag('user_role', properties.role);
        }
    } catch (error) {
        // Clarity might not be initialized
    }
}

/**
 * Helper to categorize users by credit balance
 */
function getCreditTier(credits: number): string {
    if (credits === 0) return 'free';
    if (credits < 100) return 'starter';
    if (credits < 500) return 'growth';
    if (credits < 1000) return 'pro';
    return 'enterprise';
}

/**
 * Track generation events with rich context
 */
export function trackGeneration(type: 'image' | 'video', properties: {
    prompt?: string;
    model?: string;
    resolution?: string;
    duration?: number;
    creditsUsed?: number;
    success: boolean;
    error?: string;
}): void {
    trackEvent(`generation_${type}`, {
        ...properties,
        timestamp: new Date().toISOString(),
    });
    
    // Update generation count in PostHog
    posthog.people.increment(`total_${type}_generations`);
    
    if (properties.creditsUsed) {
        posthog.people.increment('total_credits_spent', properties.creditsUsed);
    }
}

/**
 * Track purchase/billing events
 */
export function trackPurchase(properties: {
    packageId: string;
    packageName: string;
    credits: number;
    amount: number;
    currency?: string;
}): void {
    trackEvent('purchase_completed', {
        ...properties,
        currency: properties.currency || 'BRL',
        timestamp: new Date().toISOString(),
    });
    
    // Update lifetime value in PostHog
    posthog.people.increment('lifetime_value', properties.amount);
    posthog.people.increment('total_purchases');
}

// Export for direct access if needed
export { posthog, Clarity };
